import { useEffect } from 'preact/hooks';
import { currentPath, matchRoute } from './router';
import { Player, NowPlayingDeck } from './components/Player';
import { Icon } from './components/Icon';
import { Home } from './views/Home';
import { Search } from './views/Search';
import { Library } from './views/Library';
import { Podcast } from './views/Podcast';
import { Episode } from './views/Episode';
import { Queue } from './views/Queue';
import { Playlist } from './views/Playlist';
import { Person } from './views/Person';
import { Settings } from './views/Settings';
import { DeleteAccountPage } from './views/DeleteAccountPage';
import { Privacy } from './views/Privacy';
import { currentEpisode, activeLiveChat, liveChatOpen } from './state/player';
import { refreshQueue } from './state/queue';
import { isSignedIn, loadSession, sessionLoading } from './state/session';
import { handlePlaybackShortcut } from './lib/keyboardShortcuts';
import { announcement } from './lib/announce';
import { isUltrawide } from './lib/media';
import { PersistentLiveChat } from './components/LiveChat';

export function App() {
  useEffect(() => {
    void loadSession().then(() => refreshQueue());
  }, []);

  useEffect(() => {
    addEventListener('keydown', handlePlaybackShortcut);
    return () => removeEventListener('keydown', handlePlaybackShortcut);
  }, []);

  const route = matchRoute(currentPath.value);

  const hasChatSidebar = Boolean(activeLiveChat.value && liveChatOpen.value);

  return (
    <div
      class={`app ${currentEpisode.value ? 'has-player' : ''} ${hasChatSidebar ? 'has-chat-sidebar' : ''}`}
    >
      <a class="skip-link" href="#main">
        Skip to content
      </a>

      <Sidebar />

      <main id="main" class="app__main" tabIndex={-1}>
        <AuthBanner />
        <Route route={route} />
      </main>

      {/*
        Only on ultrawide viewports, where the app would otherwise leave a third of the screen
        empty. Gated in JS rather than left to the stylesheet's `display: none`, because a
        hidden deck still *mounts* — and its transcript panel would fetch a transcript over
        the network on a phone that is never going to show it. Kept outside <main> so it is a
        sibling landmark rather than page content that changes on every route change.
      */}
      {isUltrawide.value && <NowPlayingDeck />}

      {/*
        Persistent Live Chat across route changes.
        Renders as a persistent sidebar on desktop (>= 1024px) or drawer on mobile.
      */}
      <PersistentLiveChat />

      <TabBar />

      {/*
        Mounted at the root, outside the router outlet, so a route change never unmounts
        the <audio> element mid-episode.
      */}
      <Player />

      <LiveRegion />
    </div>
  );
}

/**
 * The app's single polite live region.
 *
 * Rendered once, at the root, and never unmounted: assistive tech watches regions that
 * already exist in the DOM, so a region created at announce time is announced unreliably or
 * not at all. Everything that changes state without changing what the eye is looking at —
 * queue edits, subscription toggles, background sync — pushes its text through
 * `lib/announce` and lands here.
 */
function LiveRegion() {
  const { message, token } = announcement.value;
  return (
    <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {/*
        Keyed on the token, not just rendered from the message. Queueing two episodes in a
        row produces the same string twice; without the key Preact diffs the text node, finds
        it unchanged, mutates no DOM, and assistive tech stays silent on the second one. The
        key forces the old node to be discarded and a new one inserted, which is the mutation
        the live region is actually watching for.
      */}
      <span key={token}>{message}</span>
    </div>
  );
}

function Route({ route }: { route: ReturnType<typeof matchRoute> }) {
  switch (route.name) {
    case 'home':
      return <Home />;
    case 'search':
      return <Search />;
    case 'library':
      return <Library />;
    case 'queue':
      return <Queue />;
    case 'settings':
      return <Settings />;
    case 'deleteAccount':
      return <DeleteAccountPage />;
    case 'privacy':
      return <Privacy />;
    case 'podcast':
      return <Podcast id={route.params.id} />;
    case 'episode':
      return <Episode id={route.params.id} />;
    case 'playlist':
      return <Playlist id={route.params.id} />;
    case 'person':
      return <Person name={route.params.name} />;
    default:
      return (
        <div class="view">
          <h1>Not found</h1>
          <p class="muted">
            That page does not exist. <a href="/">Go home</a>.
          </p>
        </div>
      );
  }
}

/** Surface the outcome of a magic-link round trip, which returns via a redirect. */
function AuthBanner() {
  const params = new URLSearchParams(currentPath.value.split('?')[1] ?? '');
  const auth = params.get('auth');
  if (!auth) return null;

  const messages: Record<string, string> = {
    ok: 'Signed in.',
    invalid: 'That sign-in link is invalid or has expired. Request a new one.',
    used: 'That sign-in link has already been used. Request a new one.',
    conflict: 'You are already signed in as someone else. Sign out first, then use the link again.',
  };
  const message = messages[auth];
  if (!message) return null;

  return (
    <p class={auth === 'ok' ? 'notice' : 'error'} role="status">
      {message}
    </p>
  );
}

const DESKTOP_NAV = [
  { href: '/', label: 'Home', icon: 'home' as const, match: 'home' },
  { href: '/search', label: 'Search', icon: 'search' as const, match: 'search' },
  { href: '/library', label: 'Library', icon: 'library' as const, match: 'library' },
  { href: '/queue', label: 'Queue', icon: 'queue' as const, match: 'queue' },
  { href: '/settings', label: 'Settings', icon: 'settings' as const, match: 'settings' },
];

const MOBILE_NAV = [
  { href: '/', label: 'Home', icon: 'home' as const, match: 'home' },
  { href: '/search', label: 'Search', icon: 'search' as const, match: 'search' },
  { href: '/library', label: 'Library', icon: 'library' as const, match: 'library' },
  { href: '/queue', label: 'Queue', icon: 'queue' as const, match: 'queue' },
];

/**
 * Navigation for everything wider than a phone.
 *
 * Two shapes, one component: a full sidebar on desktop, and — between roughly a tablet and a
 * small laptop — a compact icon rail, which is purely a matter of the stylesheet collapsing
 * the labels. The labels stay in the DOM rather than being swapped for `aria-label`s, so the
 * accessible name of each link is identical in both shapes and a screen-reader user never
 * sees the layout change at all.
 */
function Sidebar() {
  const route = matchRoute(currentPath.value);
  return (
    <nav class="sidebar" aria-label="Main">
      <div class="sidebar__brand">
        <span class="sidebar__logo" aria-hidden="true">
          ◉
        </span>
        <span class="sidebar__label">Podcastie</span>
      </div>
      <ul>
        {DESKTOP_NAV.map((item) => (
          <li key={item.href}>
            <a
              href={item.href}
              class={route.name === item.match ? 'is-active' : ''}
              aria-current={route.name === item.match ? 'page' : undefined}
            >
              <Icon name={item.icon} size={20} />
              <span class="sidebar__label">{item.label}</span>
            </a>
          </li>
        ))}
      </ul>
      {!sessionLoading.value && !isSignedIn.value && (
        <p class="sidebar__note muted">
          <a href="/settings">Sign in</a> to subscribe and sync.
        </p>
      )}
    </nav>
  );
}

/**
 * Phone navigation, sitting docked beneath the mini-player and clear of the home indicator.
 *
 * Rendered with the 4 primary destinations (Home, Search, Library, Queue).
 * Settings is available from the top header / profile icons on views.
 */
function TabBar() {
  const route = matchRoute(currentPath.value);
  return (
    <nav class="tabbar" aria-label="Main, compact">
      {MOBILE_NAV.map((item) => (
        <a
          key={item.href}
          href={item.href}
          class={route.name === item.match ? 'is-active' : ''}
          aria-current={route.name === item.match ? 'page' : undefined}
        >
          <Icon name={item.icon} size={22} />
          <span>{item.label}</span>
        </a>
      ))}
    </nav>
  );
}
